/**
 * Add or edit one form field or follow-up question on a category (or on the
 * whole desk). A "show if" rule makes the question appear only when an earlier
 * answer is one of the chosen values.
 */

import { useState } from 'react';
import type { SdAudience, SdFieldOption, SdFieldType, SdFormField } from '@ampersandbrands/ui';
import { FilterLabel } from '@/components/FilterLabel';
import { useCreateField, useUpdateField } from '@/lib/useServiceDesk';
import type { SdFieldAdmin, SdFieldInput } from '@/lib/serviceDeskTypes';
import { SdError } from './SdShell';
import { AUDIENCE_LABELS, EDITABLE_AUDIENCES } from './sdLabels';
import { SD_BTN_GHOST, SD_BTN_PRIMARY, SD_INPUT, SD_MUTED } from './sdStyles';

const TYPE_LABELS: Record<SdFieldType, string> = {
  text: 'Short answer',
  textarea: 'Long answer',
  select: 'Pick one',
  multiselect: 'Pick several',
  yesno: 'Yes or no',
  date: 'Date',
  number: 'Number',
  person: 'Person from the directory',
  store: 'Store',
  asset_checklist: 'Equipment checklist',
  notice: 'Message only (no answer)',
};

const slug = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60);

const optionsToText = (o: SdFieldOption[] | null | undefined) =>
  (o ?? []).map((x) => (x.value === slug(x.label) ? x.label : `${x.value}|${x.label}`)).join('\n');

function textToOptions(text: string): SdFieldOption[] {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const [a, b] = l.split('|');
      return b !== undefined ? { value: a!.trim(), label: b.trim() } : { value: slug(l), label: l };
    });
}

export function FieldForm({
  categoryId,
  field,
  earlier,
  nextSort,
  onDone,
}: {
  /** null = a desk-wide field. */
  categoryId: number | null;
  field: SdFieldAdmin | null;
  /** Fields that come before this one on the form: the only valid "show if" sources. */
  earlier: SdFormField[];
  nextSort: number;
  onDone: () => void;
}) {
  const create = useCreateField();
  const update = useUpdateField();
  const [label, setLabel] = useState(field?.label ?? '');
  const [key, setKey] = useState(field?.field_key ?? '');
  const [keyTouched, setKeyTouched] = useState(!!field);
  const [type, setType] = useState<SdFieldType>(field?.field_type ?? 'text');
  const [help, setHelp] = useState(field?.help_text ?? '');
  const [required, setRequired] = useState(field?.required ?? false);
  const [optionsText, setOptionsText] = useState(optionsToText(field?.options));
  const [personScope, setPersonScope] = useState<'store' | 'corp' | 'all'>(field?.person_scope ?? 'all');
  const [allowCorp, setAllowCorp] = useState(field?.allow_corporate ?? false);
  const [audience, setAudience] = useState<SdAudience[]>(field?.audience ?? []);
  const [showKey, setShowKey] = useState(field?.show_if?.field_key ?? '');
  const [showEquals, setShowEquals] = useState<string[]>(field?.show_if?.equals ?? []);

  const source = earlier.find((f) => f.field_key === showKey);
  const sourceChoices: SdFieldOption[] =
    source?.field_type === 'yesno'
      ? [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ]
      : (source?.options ?? []);

  const input: SdFieldInput = {
    category_id: categoryId,
    field_key: key.trim(),
    label: label.trim(),
    help_text: help.trim() || null,
    field_type: type,
    options: type === 'select' || type === 'multiselect' ? textToOptions(optionsText) : null,
    person_scope: type === 'person' ? personScope : null,
    allow_corporate: type === 'store' ? allowCorp : null,
    required: type === 'notice' ? false : required,
    show_if: showKey && showEquals.length > 0 ? { field_key: showKey, equals: showEquals } : null,
    audience: audience.length > 0 ? audience : null,
    sort: field?.sort ?? nextSort,
  };

  const needsOptions = type === 'select' || type === 'multiselect';
  const canSave =
    input.label.length > 0 && input.field_key.length > 0 && (!needsOptions || (input.options?.length ?? 0) > 0);
  const busy = create.isPending || update.isPending;

  return (
    <div className="mt-3 flex flex-col gap-3 rounded-md border border-amp-line p-3 dark:border-amp-dark-line">
      <div>
        <FilterLabel htmlFor="sd-f-label">Question or field label</FilterLabel>
        <input
          id="sd-f-label"
          value={label}
          onChange={(e) => {
            setLabel(e.target.value);
            if (!keyTouched) setKey(slug(e.target.value));
          }}
          className={SD_INPUT}
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <FilterLabel htmlFor="sd-f-type">Answer type</FilterLabel>
          <select id="sd-f-type" value={type} onChange={(e) => setType(e.target.value as SdFieldType)} className={SD_INPUT}>
            {(Object.keys(TYPE_LABELS) as SdFieldType[]).map((t) => (
              <option key={t} value={t}>
                {TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <FilterLabel htmlFor="sd-f-key">Internal name</FilterLabel>
          <input
            id="sd-f-key"
            value={key}
            disabled={!!field}
            onChange={(e) => {
              setKey(slug(e.target.value));
              setKeyTouched(true);
            }}
            className={SD_INPUT}
          />
        </div>
      </div>
      <div>
        <FilterLabel htmlFor="sd-f-help">Help text under the question</FilterLabel>
        <input id="sd-f-help" value={help} onChange={(e) => setHelp(e.target.value)} className={SD_INPUT} />
      </div>
      {needsOptions && (
        <div>
          <FilterLabel htmlFor="sd-f-options">Choices, one per line</FilterLabel>
          <textarea
            id="sd-f-options"
            rows={4}
            value={optionsText}
            onChange={(e) => setOptionsText(e.target.value)}
            className={SD_INPUT}
          />
        </div>
      )}
      {type === 'person' && (
        <div>
          <FilterLabel htmlFor="sd-f-scope">Who can be picked</FilterLabel>
          <select
            id="sd-f-scope"
            value={personScope}
            onChange={(e) => setPersonScope(e.target.value as 'store' | 'corp' | 'all')}
            className={SD_INPUT}
          >
            <option value="all">Anyone</option>
            <option value="store">People at the requester's store</option>
            <option value="corp">Corporate staff</option>
          </select>
        </div>
      )}
      {type === 'store' && (
        <label className="flex items-center gap-2 text-[13px] text-amp-ink dark:text-amp-dark-ink">
          <input type="checkbox" checked={allowCorp} onChange={(e) => setAllowCorp(e.target.checked)} />
          Allow "Corporate" as an answer
        </label>
      )}
      {type !== 'notice' && (
        <label className="flex items-center gap-2 text-[13px] text-amp-ink dark:text-amp-dark-ink">
          <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} />
          Required
        </label>
      )}
      <div>
        <FilterLabel as="div">Shown to (none checked means everyone who can file here)</FilterLabel>
        <div className="flex flex-wrap gap-4 text-[13px] text-amp-ink dark:text-amp-dark-ink">
          {EDITABLE_AUDIENCES.map((a) => (
            <label key={a} className="flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={audience.includes(a)}
                onChange={() => setAudience((c) => (c.includes(a) ? c.filter((x) => x !== a) : [...c, a]))}
              />
              {AUDIENCE_LABELS[a]}
            </label>
          ))}
        </div>
      </div>

      <div>
        <FilterLabel htmlFor="sd-f-showif">Only ask this when an earlier answer matches</FilterLabel>
        {earlier.length === 0 ? (
          <p className={`text-[12px] ${SD_MUTED}`}>There are no earlier questions to depend on.</p>
        ) : (
          <select
            id="sd-f-showif"
            value={showKey}
            onChange={(e) => {
              setShowKey(e.target.value);
              setShowEquals([]);
            }}
            className={SD_INPUT}
          >
            <option value="">Always ask</option>
            {earlier.map((f) => (
              <option key={f.field_key} value={f.field_key}>
                {f.label}
              </option>
            ))}
          </select>
        )}
        {showKey && sourceChoices.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-4 text-[13px] text-amp-ink dark:text-amp-dark-ink">
            {sourceChoices.map((c) => (
              <label key={c.value} className="flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={showEquals.includes(c.value)}
                  onChange={() =>
                    setShowEquals((cur) => (cur.includes(c.value) ? cur.filter((x) => x !== c.value) : [...cur, c.value]))
                  }
                />
                {c.label}
              </label>
            ))}
          </div>
        )}
        {showKey && sourceChoices.length === 0 && (
          <input
            aria-label="Matching answers, separated by commas"
            placeholder="Matching answers, separated by commas"
            value={showEquals.join(', ')}
            onChange={(e) =>
              setShowEquals(
                e.target.value
                  .split(',')
                  .map((v) => v.trim())
                  .filter(Boolean),
              )
            }
            className={`${SD_INPUT} mt-2`}
          />
        )}
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          disabled={!canSave || busy}
          className={SD_BTN_PRIMARY}
          onClick={async () => {
            if (field) await update.mutateAsync({ id: field.id, patch: input });
            else await create.mutateAsync(input);
            onDone();
          }}
        >
          {field ? 'Save field' : 'Add field'}
        </button>
        <button type="button" onClick={onDone} className={SD_BTN_GHOST}>
          Cancel
        </button>
      </div>
      <SdError error={create.error ?? update.error} what="save the field" />
    </div>
  );
}
